import React from "react";
import Icon from "../../../components/AppIcon";

const HistoricalComparison = ({ weekData, monthData, todayAmount, loading }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(value || 0);
  };

  // ── Date range helpers (mirrors backend logic) ───────────────────────────
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const addDays = (date, n) => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
  };

  const fmtShort = (date) =>
    date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

  // Week: Monday → today  vs.  Monday last week → same weekday last week
  const dayOfWeek = today.getDay(); // 0=Sun … 6=Sat
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const thisWeekStart = addDays(today, -daysFromMonday);
  const lastWeekStart = addDays(thisWeekStart, -7);
  const lastWeekEnd   = addDays(today, -7);

  const weekLabel     = `${fmtShort(thisWeekStart)} – ${fmtShort(today)}`;
  const prevWeekLabel = `${fmtShort(lastWeekStart)} – ${fmtShort(lastWeekEnd)}`;

  // Month: 1st → today  vs.  1st last month → same day last month (clamped)
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthMaxDay = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
  const lastMonthDay = Math.min(today.getDate(), lastMonthMaxDay);
  const lastMonthEnd = new Date(lastMonthStart.getFullYear(), lastMonthStart.getMonth(), lastMonthDay);

  const monthLabel     = `${fmtShort(thisMonthStart)} – ${fmtShort(today)}`;
  const prevMonthLabel = `${fmtShort(lastMonthStart)} – ${fmtShort(lastMonthEnd)}`;
  // ─────────────────────────────────────────────────────────────────────────

  const getChangeDisplay = (change, changePercent, hasPreviousData) => {
    // If no previous data exists, show as new data
    if (!hasPreviousData) {
      return {
        value: null,
        trend: "neutral",
        isNew: true,
      };
    }

    // Handle edge cases
    if (change === 0 || !changePercent || !isFinite(changePercent)) {
      return {
        value: 0,
        trend: "neutral",
        isNew: false,
      };
    }

    return {
      value: Math.abs(changePercent).toFixed(1),
      trend: change > 0 ? "up" : "down",
      isNew: false,
    };
  };

  const weekChange = getChangeDisplay(
    weekData?.change,
    weekData?.changePercent,
    (weekData?.previous || 0) > 0
  );

  const monthChange = getChangeDisplay(
    monthData?.change,
    monthData?.changePercent,
    (monthData?.previous || 0) > 0
  );

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-4 md:p-6">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div
            className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "rgba(30, 58, 95, 0.1)" }}
          >
            <Icon
              name="TrendingUp"
              size={20}
              color="var(--color-primary)"
              className="md:w-6 md:h-6"
            />
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              Historical Comparison
            </h3>
            <p className="text-xs md:text-sm font-caption text-muted-foreground">
              TRONC performance trends
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="spinner" />
          <span className="ml-3 text-muted-foreground">
            Loading historical data...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div
          className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: "rgba(30, 58, 95, 0.1)" }}
        >
          <Icon
            name="TrendingUp"
            size={20}
            color="var(--color-primary)"
            className="md:w-6 md:h-6"
          />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Historical Comparison
          </h3>
          <p className="text-xs md:text-sm font-caption text-muted-foreground">
            TRONC performance trends
          </p>
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        {/* Today */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs md:text-sm font-caption text-muted-foreground">Today</p>
            <span className="text-xs font-caption text-muted-foreground">
              {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "short" })}
            </span>
          </div>
          <p className="text-xl md:text-2xl lg:text-3xl font-data font-semibold text-foreground">
            {formatCurrency(todayAmount || 0)}
          </p>
          <div className="flex items-center gap-2 text-xs md:text-sm font-caption text-muted-foreground mt-2">
            <Icon name="Clock" size={14} />
            <span>Current day total</span>
          </div>
        </div>

        {/* This Week */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs md:text-sm font-caption text-muted-foreground mb-1">
                This Week
                <span className="ml-1 text-xs opacity-70">({weekLabel})</span>
              </p>
              <p className="text-xl md:text-2xl lg:text-3xl font-data font-semibold text-foreground">
                {formatCurrency(weekData?.current)}
              </p>
            </div>
            <div className="text-right">
              {weekChange?.value !== null ? (
                <>
                  <div className="flex items-center gap-2 justify-end mb-1">
                    <Icon
                      name={
                        weekChange?.trend === "up"
                          ? "ArrowUp"
                          : weekChange?.trend === "down"
                            ? "ArrowDown"
                            : "Minus"
                      }
                      size={16}
                      color={
                        weekChange?.trend === "up"
                          ? "var(--color-success)"
                          : weekChange?.trend === "down"
                            ? "var(--color-error)"
                            : "var(--color-muted-foreground)"
                      }
                    />
                    <span
                      className="text-sm md:text-base font-data font-semibold"
                      style={{
                        color:
                          weekChange?.trend === "up"
                            ? "var(--color-success)"
                            : weekChange?.trend === "down"
                              ? "var(--color-error)"
                              : "var(--color-muted-foreground)",
                      }}
                    >
                      {weekChange?.value}%
                    </span>
                  </div>
                  <p className="text-xs font-caption text-muted-foreground">
                    vs {prevWeekLabel}
                  </p>
                </>
              ) : (
                <div>
                  <div className="flex items-center gap-2 justify-end mb-1">
                    <Icon
                      name="Minus"
                      size={16}
                      color="var(--color-muted-foreground)"
                    />
                    <span className="text-sm md:text-base font-data font-semibold text-muted-foreground">
                      {weekChange?.isNew ? "New" : "N/A"}
                    </span>
                  </div>
                  <p className="text-xs font-caption text-muted-foreground">
                    {weekChange?.isNew ? "No previous data" : "vs last week"}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs md:text-sm font-caption text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>
              {prevWeekLabel}: {formatCurrency(weekData?.previous)}
            </span>
          </div>
        </div>

        {/* This Month */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs md:text-sm font-caption text-muted-foreground mb-1">
                This Month
                <span className="ml-1 text-xs opacity-70">({monthLabel})</span>
              </p>
              <p className="text-xl md:text-2xl lg:text-3xl font-data font-semibold text-foreground">
                {formatCurrency(monthData?.current)}
              </p>
            </div>
            <div className="text-right">
              {monthChange?.value !== null ? (
                <>
                  <div className="flex items-center gap-2 justify-end mb-1">
                    <Icon
                      name={
                        monthChange?.trend === "up"
                          ? "ArrowUp"
                          : monthChange?.trend === "down"
                            ? "ArrowDown"
                            : "Minus"
                      }
                      size={16}
                      color={
                        monthChange?.trend === "up"
                          ? "var(--color-success)"
                          : monthChange?.trend === "down"
                            ? "var(--color-error)"
                            : "var(--color-muted-foreground)"
                      }
                    />
                    <span
                      className="text-sm md:text-base font-data font-semibold"
                      style={{
                        color:
                          monthChange?.trend === "up"
                            ? "var(--color-success)"
                            : monthChange?.trend === "down"
                              ? "var(--color-error)"
                              : "var(--color-muted-foreground)",
                      }}
                    >
                      {monthChange?.value}%
                    </span>
                  </div>
                  <p className="text-xs font-caption text-muted-foreground">
                    vs {prevMonthLabel}
                  </p>
                </>
              ) : (
                <div>
                  <div className="flex items-center gap-2 justify-end mb-1">
                    <Icon
                      name="Minus"
                      size={16}
                      color="var(--color-muted-foreground)"
                    />
                    <span className="text-sm md:text-base font-data font-semibold text-muted-foreground">
                      {monthChange?.isNew ? "New" : "N/A"}
                    </span>
                  </div>
                  <p className="text-xs font-caption text-muted-foreground">
                    {monthChange?.isNew ? "No previous data" : "vs last month"}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs md:text-sm font-caption text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>
              {prevMonthLabel}: {formatCurrency(monthData?.previous)}
            </span>
          </div>
        </div>

        {/* Daily Average and Peak */}
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="bg-background rounded-lg p-3 md:p-4 border border-border">
            <p className="text-xs font-caption text-muted-foreground mb-1">
              Daily Average
            </p>
            <p className="text-base md:text-lg font-data font-semibold text-foreground">
              {formatCurrency(weekData?.average)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              This week
            </p>
          </div>
          <div className="bg-background rounded-lg p-3 md:p-4 border border-border">
            <p className="text-xs font-caption text-muted-foreground mb-1">
              Peak Day
            </p>
            <p className="text-base md:text-lg font-data font-semibold text-foreground">
              {formatCurrency(weekData?.peakDay)}
            </p>
            {weekData?.peakDayName && (
              <p className="text-xs text-muted-foreground mt-1">
                {weekData.peakDayName}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoricalComparison;
